import React, { useState, useEffect, useRef } from "react";
import { Spin } from "antd";
import { toast } from "react-toastify";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import {
  getGlobalSpecs,
  postGlobalSpecs,
} from "../../../../actions/specManager";
import {
  buildGlobalSpecEditorRowsFromApi,
  formatSpec,
  handleCheckUnit,
  checkIfSpecsInvalid,
  globalTestSpecEditorRowsMatchForDraft,
  globalTestSpecEditorHasMrlCountryListData,
  globalTestSpecMrlMissingSelectedMarkets,
  buildGlobalSpecsUpdateRequestBody,
  isCustomSpecProduct,
  snapshotGlobalspecCustomProductForRestore,
  applyGlobalspecCustomSnapshotToProduct,
  GLOBAL_TEST_SPEC_MODE,
  SPEC_FILTER_VALUES,
  canEditSpecs,
} from "../utils/utils";
import { DiscardChangesModal } from "../shared/specManagerUi";
import TestSpecListPanel from "./TestSpecListPanel";
import TestSpecOverview from "./TestSpecOverview";
import TestSpecEditor from "./TestSpecEditor";
import ProductSpecificSpecsModal from "./ProductSpecificSpecsModal";
import SaveSuccessModal from "./SaveSuccessModal";

const sortedProductIds = (products) => [
  ...products.map((productRow) => productRow.product_id).filter(Boolean),
]
  .map((id) => String(id))
  .sort();

export default function TestSpecManager({
  customer,
  identifierFields,
  onEditModeChange,
  onDataRefresh,
}) {
  const [mode, setMode] = useState(GLOBAL_TEST_SPEC_MODE.LIST);
  const [testRows, setTestRows] = useState([]);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [appliedSearchValue, setAppliedSearchValue] = useState("");
  const [activeFilter, setActiveFilter] = useState(SPEC_FILTER_VALUES.ALL);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextPageLink, setNextPageLink] = useState("");
  const [selectedTestName, setSelectedTestName] = useState("");
  const [testSpecStatus, setTestSpecStatus] = useState("added");
  const [specRows, setSpecRows] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loadingEditData, setLoadingEditData] = useState(false);
  const [detailLoadFailed, setDetailLoadFailed] = useState(false);
  const [specDataChanged, setSpecDataChanged] = useState(false);
  const [step1Baseline, setStep1Baseline] = useState(null);
  const [isPreviewStep, setIsPreviewStep] = useState(false);
  const [productSearchValue, setProductSearchValue] = useState("");
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [productSpecificModalOpen, setProductSpecificModalOpen] = useState(false);
  const [productSpecificModalVariant, setProductSpecificModalVariant] = useState("mixed");
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccessModal, setSaveSuccessModal] = useState({
    open: false,
    testDisplayName: "",
    saveAction: "update",
    affectedProductCount: 0,
  });

  const abortControllerRef = useRef(null);
  const customSnapshotsRef = useRef(new Map());

  const canEdit = canEditSpecs();

  const rebuildCustomSnapshots = (products) => {
    const nextMap = new Map();
    products.forEach((productRow) => {
      const snapshot = snapshotGlobalspecCustomProductForRestore(productRow);
      if (snapshot) nextMap.set(productRow.product_id, snapshot);
    });
    customSnapshotsRef.current = nextMap;
  };

  const seedStep1Baseline = (productsSnapshot, specSnapshot) => {
    setStep1Baseline({
      products: cloneDeep(productsSnapshot),
      spec: cloneDeep(specSnapshot),
    });
    setSpecDataChanged(false);
  };

  const hasDraftChanges = () => {
    if (!step1Baseline) return false;
    const baselineIds = sortedProductIds(step1Baseline.products);
    const currentIds = sortedProductIds(selectedProducts);
    const productsChanged = !isEqual(baselineIds, currentIds);
    return productsChanged || specDataChanged;
  };

  const fetchTestList = async ({
    filter = activeFilter,
    search = appliedSearchValue,
    page = "1",
    append = false,
  } = {}) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    if (append) setLoadingMore(true);
    else setLoading(true);

    const response = await getGlobalSpecs(
      { filter, search, page },
      abortController.signal,
      customer,
    );

    if (response.canceled) return;

    if (response.success && Array.isArray(response.result)) {
      setTestRows((previous) => (append ? [...previous, ...response.result] : response.result));
      setNextPageLink(response.pages.nextPageLink);
    } else if (!append) {
      setTestRows([]);
      setNextPageLink("");
      if (!response.canceled && response.message) {
        toast.error(response.message);
      }
    }

    setLoading(false);
    setLoadingMore(false);
  };

  useEffect(() => {
    fetchTestList();
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const isEditing = mode !== GLOBAL_TEST_SPEC_MODE.LIST;
    onEditModeChange(isEditing);
  }, [mode, onEditModeChange]);

  const openTestEditor = async (testName, status) => {
    setSelectedTestName(testName);
    setTestSpecStatus(status === "pending" ? "pending" : "added");
    setMode(GLOBAL_TEST_SPEC_MODE.VIEW);
    setDetailLoadFailed(false);
    setLoadingEditData(true);
    setSpecRows([]);
    setSelectedProducts([]);
    rebuildCustomSnapshots([]);
    setStep1Baseline(null);
    setIsPreviewStep(false);
    setProductSearchValue("");

    const response = await getGlobalSpecs(
      { test_display_name: testName },
      null,
      customer,
    );

    if (!response.success) {
      toast.error(response.message);
      setDetailLoadFailed(true);
      setLoadingEditData(false);
      return;
    }

    const products = response.result.products != null ? response.result.products : [];
    setSelectedProducts(products);
    rebuildCustomSnapshots(products);

    const specToUse = buildGlobalSpecEditorRowsFromApi(response.result, testName);
    setSpecRows(specToUse);

    setLoadingEditData(false);
  };

  const handleOpenEditMode = () => {
    if (!canEdit) return;
    setProductSearchValue("");
    seedStep1Baseline(selectedProducts, specRows);
    setMode(testSpecStatus === "pending" ? GLOBAL_TEST_SPEC_MODE.ADD : GLOBAL_TEST_SPEC_MODE.EDIT);
  };

  const handleSpecRowsChange = (updatedRows) => {
    const nextRows = cloneDeep(updatedRows);
    setSpecRows(nextRows);
    if (step1Baseline) {
      const matchesBaseline = globalTestSpecEditorRowsMatchForDraft(
        nextRows,
        step1Baseline.spec,
      );
      setSpecDataChanged(!matchesBaseline);
    }
  };

  const handleSelectedProductsChange = (products) => {
    const merged = products.map((productRow) => {
      const snapshot = customSnapshotsRef.current.get(productRow.product_id);
      return applyGlobalspecCustomSnapshotToProduct(productRow, snapshot);
    });
    setSelectedProducts(merged);
    if (step1Baseline) {
      const baselineIds = sortedProductIds(step1Baseline.products);
      const currentIds = sortedProductIds(merged);
      const productsChanged = !isEqual(baselineIds, currentIds);
      const specMatchesBaseline = globalTestSpecEditorRowsMatchForDraft(
        specRows,
        step1Baseline.spec,
      );
      setSpecDataChanged(productsChanged || !specMatchesBaseline);
    }
  };

  const buildPostPayload = (updateOperation) => {
    const formattedRows = handleCheckUnit(formatSpec(specRows));
    const firstSpec = formattedRows[0];
    return buildGlobalSpecsUpdateRequestBody({
      testDisplayName: selectedTestName,
      formattedFirstSpec: firstSpec,
      updateOperation,
      products: selectedProducts,
      includeMrlCountriesList: globalTestSpecEditorHasMrlCountryListData(specRows),
    });
  };

  const finalizeSave = async (payload, saveMeta) => {
    const result = await postGlobalSpecs(payload, customer);
    if (!result.success) {
      toast.error(result.message);
      return false;
    }
    setSaveSuccessModal({
      open: true,
      testDisplayName: selectedTestName,
      saveAction: saveMeta.saveAction,
      affectedProductCount: saveMeta.affectedProductCount,
    });
    setProductSpecificModalOpen(false);
    return true;
  };

  const handleSaveClick = () => {
    if (!canEdit) {
      toast.error("You do not have permission to edit test specs.");
      return;
    }
    if (globalTestSpecMrlMissingSelectedMarkets(specRows)) {
      toast.error("Please select at least one country for MRL tests.");
      return;
    }
    if (checkIfSpecsInvalid(specRows)) {
      toast.error("Invalid or missing value(s).");
      return;
    }

    const customCount = selectedProducts.filter(isCustomSpecProduct).length;
    const standardCount = selectedProducts.length - customCount;
    let modalVariant = "mixed";
    if (customCount === 0) modalVariant = "standardOnly";
    else if (standardCount === 0) modalVariant = "customOnly";

    setProductSpecificModalVariant(modalVariant);
    setProductSpecificModalOpen(true);
  };

  const handleModalApplyStandardOnly = async () => {
    setIsSaving(true);
    const customCount = selectedProducts.filter(isCustomSpecProduct).length;
    const standardCount = selectedProducts.length - customCount;
    const payload = buildPostPayload("standard_only");
    await finalizeSave(payload, {
      saveAction: mode === GLOBAL_TEST_SPEC_MODE.ADD ? "add" : "update",
      affectedProductCount: standardCount,
    });
    setIsSaving(false);
  };

  const handleModalApplyAllSelected = async () => {
    setIsSaving(true);
    const payload = buildPostPayload("all_selected");
    await finalizeSave(payload, {
      saveAction: mode === GLOBAL_TEST_SPEC_MODE.ADD ? "add" : "update",
      affectedProductCount: selectedProducts.length,
    });
    setIsSaving(false);
  };

  const handlePreviewStepChange = (nextIsPreviewStep) => {
    if (nextIsPreviewStep) {
      setProductSearchValue("");
    }
    setIsPreviewStep(nextIsPreviewStep);
  };

  const exitToList = () => {
    setMode(GLOBAL_TEST_SPEC_MODE.LIST);
    setSelectedTestName("");
    setSpecRows([]);
    setSelectedProducts([]);
    rebuildCustomSnapshots([]);
    setSpecDataChanged(false);
    setStep1Baseline(null);
    setIsPreviewStep(false);
    setProductSearchValue("");
    setDetailLoadFailed(false);
  };

  const handleSaveSuccessModalClose = () => {
    setSaveSuccessModal({
      open: false,
      testDisplayName: "",
      saveAction: "update",
      affectedProductCount: 0,
    });
    exitToList();
    fetchTestList();
    if (onDataRefresh) onDataRefresh();
  };

  const handleBack = () => {
    if (hasDraftChanges()) {
      setShowDiscardModal(true);
    } else {
      exitToList();
    }
  };

  const handleLoadMore = () => {
    if (!nextPageLink || loadingMore || loading) return;
    const page = nextPageLink.split("page=")[1].split("&")[0];
    fetchTestList({ page, append: true });
  };

  const handleSearchSubmit = (submittedSearch) => {
    const nextSearch = submittedSearch != null ? submittedSearch : searchInputValue;
    setSearchInputValue(nextSearch);
    setAppliedSearchValue(nextSearch);
    fetchTestList({ search: nextSearch });
  };

  const handleSearchInputChange = (nextInputValue) => {
    setSearchInputValue(nextInputValue);
    if (nextInputValue === "" && appliedSearchValue !== "") {
      setAppliedSearchValue("");
      fetchTestList({ search: "" });
    }
  };

  if (mode === GLOBAL_TEST_SPEC_MODE.LIST) {
    return (
      <TestSpecListPanel
        testRows={testRows}
        loading={loading}
        loadingMore={loadingMore}
        searchInputValue={searchInputValue}
        activeFilter={activeFilter}
        onSearchInputChange={handleSearchInputChange}
        onSearchSubmit={handleSearchSubmit}
        onFilterChange={(filter) => {
          setActiveFilter(filter);
          fetchTestList({ filter });
        }}
        onTestRowClick={openTestEditor}
        onLoadMore={handleLoadMore}
      />
    );
  }

  if (mode === GLOBAL_TEST_SPEC_MODE.VIEW) {
    return (
      <>
        {loadingEditData ? (
          <div className="SpecManager_LoadingCenter"><Spin /></div>
        ) : (
          <TestSpecOverview
            testName={selectedTestName}
            specRows={specRows}
            products={selectedProducts}
            identifierFields={identifierFields}
            testSpecStatus={testSpecStatus}
            loadFailed={detailLoadFailed}
            onEditClick={handleOpenEditMode}
            onBack={handleBack}
          />
        )}
        <DiscardChangesModal
          open={showDiscardModal}
          onCancel={() => setShowDiscardModal(false)}
          onConfirm={() => {
            setShowDiscardModal(false);
            exitToList();
          }}
        />
      </>
    );
  }

  return (
    <>
      {loadingEditData ? (
        <div className="SpecManager_LoadingCenter"><Spin /></div>
      ) : (
        <TestSpecEditor
          customer={customer}
          identifierFields={identifierFields}
          testName={selectedTestName}
          specRows={specRows}
          selectedProducts={selectedProducts}
          customSnapshotsRef={customSnapshotsRef}
          isPreviewStep={isPreviewStep}
          hasDraftChanges={hasDraftChanges()}
          productSearchValue={productSearchValue}
          onProductSearchChange={setProductSearchValue}
          onSpecRowsChange={handleSpecRowsChange}
          onSelectedProductsChange={handleSelectedProductsChange}
          onPreviewStepChange={handlePreviewStepChange}
          onSaveClick={handleSaveClick}
          onBack={handleBack}
          saving={isSaving}
        />
      )}

      <ProductSpecificSpecsModal
        open={productSpecificModalOpen}
        variant={productSpecificModalVariant}
        customProductsCount={selectedProducts.filter(isCustomSpecProduct).length}
        loading={isSaving}
        onCancel={() => setProductSpecificModalOpen(false)}
        onApplyStandardOnly={handleModalApplyStandardOnly}
        onApplyAllSelected={handleModalApplyAllSelected}
        onSaveAllStandard={handleModalApplyStandardOnly}
      />

      <SaveSuccessModal
        open={saveSuccessModal.open}
        testDisplayName={saveSuccessModal.testDisplayName}
        saveAction={saveSuccessModal.saveAction}
        affectedProductCount={saveSuccessModal.affectedProductCount}
        onClose={handleSaveSuccessModalClose}
      />

      <DiscardChangesModal
        open={showDiscardModal}
        onCancel={() => setShowDiscardModal(false)}
        onConfirm={() => {
          setShowDiscardModal(false);
          exitToList();
        }}
      />
    </>
  );
}
